import { calculatePseudoVoigtHeight, getPseudoVoigtArea, getPseudoVoigtData, getPseudoVoigtFactor, pseudoVoigtFct, pseudoVoigtFwhmToWidth, pseudoVoigtWidthToFWHM, } from "../pseudoVoigt/PseudoVoigt.js";
/**
 * TCH-style pseudo-Voigt where gaussian and lorentzian widths are independent.
 * The effective fwhm and mixing parameter mu are derived from fwhmG and fwhmL
 * via the Thompson–Cox–Hastings approximation.
 */
export class PseudoVoigtTCH {
    _fwhmG;
    _fwhmL;
    _fwhm;
    _mu;
    _lorentzianWidthFraction;
    constructor(options = {}) {
        const { fwhmG, fwhmL, fwhm, mu = 0.5 } = options;
        this._mu = mu;
        this._fwhm = 0;
        this._fwhmG = 0;
        this._fwhmL = 0;
        this._lorentzianWidthFraction = lorentzianWidthFraction(1 - mu);
        if (fwhmG && fwhmL) {
            this._fwhmG = fwhmG;
            this.fwhmL = fwhmL;
        }
        else if (fwhm) {
            this.fwhm = fwhm;
        }
    }
    set fwhmG(value) {
        const effectiveFwhm = computeEffectiveWidth(value, this._fwhmL);
        const lorentzianFraction = this._fwhmL / effectiveFwhm;
        this._fwhm = effectiveFwhm;
        this._mu =
            1 -
                (1.36603 * lorentzianFraction -
                    0.47719 * lorentzianFraction * lorentzianFraction +
                    0.11116 * lorentzianFraction * lorentzianFraction * lorentzianFraction);
        this._fwhmG = value;
        this._lorentzianWidthFraction = lorentzianFraction;
    }
    get fwhmG() {
        return this._fwhmG;
    }
    set fwhmL(value) {
        const effectiveFwhm = computeEffectiveWidth(this._fwhmG, value);
        const lorentzianFraction = value / effectiveFwhm;
        this._fwhm = effectiveFwhm;
        this._mu =
            1 -
                (1.36603 * lorentzianFraction -
                    0.47719 * lorentzianFraction * lorentzianFraction +
                    0.11116 * lorentzianFraction * lorentzianFraction * lorentzianFraction);
        this._fwhmL = value;
        this._lorentzianWidthFraction = lorentzianFraction;
    }
    get fwhmL() {
        return this._fwhmL;
    }
    set mu(value) {
        const lorentzianFraction = lorentzianWidthFraction(1 - value);
        this._lorentzianWidthFraction = lorentzianFraction;
        this._fwhmL = this._fwhm * lorentzianFraction;
        this._fwhmG = this._fwhm * (1 - lorentzianFraction);
        this._mu = value;
    }
    get mu() {
        return this._mu;
    }
    set fwhm(value) {
        const lorentzianFraction = this._lorentzianWidthFraction || lorentzianWidthFraction(1 - this._mu);
        this._fwhmL = value * lorentzianFraction;
        this._fwhmG = value * (1 - lorentzianFraction);
        this._fwhm = value;
    }
    get fwhm() {
        return this._fwhm;
    }
    fwhmToWidth(fwhm = this._fwhm, mu = this._mu) {
        return pseudoVoigtFwhmToWidth(fwhm, mu);
    }
    widthToFWHM(width, mu = this._mu) {
        return pseudoVoigtWidthToFWHM(width, mu);
    }
    fct(x) {
        return pseudoVoigtFct(x, this._fwhm, this._mu);
    }
    getArea(height = 1) {
        return getPseudoVoigtArea({ fwhm: this._fwhm, height, mu: this._mu });
    }
    getFactor(area) {
        return getPseudoVoigtFactor(area, this._mu);
    }
    getData(options = {}) {
        const { length, factor, height = calculatePseudoVoigtHeight({
            fwhm: this._fwhm,
            mu: this._mu,
            area: 1,
        }), } = options;
        return getPseudoVoigtData(this, { factor, length, height });
    }
    calculateHeight(area = 1) {
        return calculatePseudoVoigtHeight({
            fwhm: this._fwhm,
            mu: this._mu,
            area,
        });
    }
    getParameters() {
        return ['fwhmG', 'fwhmL'];
    }
}
/**
 * Compute the effective FWHM from gaussian and lorentzian component widths
 * using the Thompson–Cox–Hastings approximation.
 * @param fwhmG - gaussian component FWHM.
 * @param fwhmL - lorentzian component FWHM.
 * @returns effective combined FWHM.
 */
function computeEffectiveWidth(fwhmG, fwhmL) {
    return ((fwhmG ** 5 +
        2.69269 * fwhmG ** 4 * fwhmL +
        2.42843 * fwhmG ** 3 * fwhmL ** 2 +
        4.47163 * fwhmG ** 2 * fwhmL ** 3 +
        0.07842 * fwhmG * fwhmL ** 4 +
        fwhmL ** 5) **
        0.2);
}
/**
 * Solve for the lorentzian width fraction fwhmL/fwhm given lorentzianFraction = 1 - mu,
 * using Newton's method on: 1.36603·x - 0.47719·x² + 0.11116·x³ = lorentzianFraction.
 * @param lorentzianFraction - TCH lorentzian mixing parameter (= 1 - mu).
 * @returns the lorentzian width fraction fwhmL/fwhm.
 */
function lorentzianWidthFraction(lorentzianFraction) {
    let fraction = lorentzianFraction;
    for (let i = 0; i < 6; i++) {
        const f = 1.36603 * fraction -
            0.47719 * fraction * fraction +
            0.11116 * fraction * fraction * fraction -
            lorentzianFraction;
        const df = 1.36603 - 2 * 0.47719 * fraction + 3 * 0.11116 * fraction * fraction;
        fraction -= f / df;
    }
    return fraction;
}
//# sourceMappingURL=PseudoVoigtTCH.js.map